import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  BaseBox,
  BaseTypography,
  BaseButton,
} from "../../Common/UI/exports";
import { saveRelease, resetStatus } from "../../APIs/operationAPI";
import {
  openRelease,
  closeRelease,
  openHotfix,
  closeHotfix,
  hotfixOpeningRollBack,
  hotfixClosingRollBack,
  releaseOpeningRollBack,
  releaseClosingRollBack,
} from "../../APIs/gitFlowReleaseManagementAPI";

function PriorityGroups() {
  const location = useLocation();
  const navigate = useNavigate();
  const repositories = location.state?.repositories || [];
  const operationId = useMemo(
    () => location.state?.operationId || "",
    [location.state?.operationId]
  );
  const loaded = location.state?.loaded || false;

  if (operationId) localStorage.setItem("operationKey", operationId);

  const selectedRepositories = repositories.filter((repo) => repo.selected);

  const groupedByPriority = selectedRepositories.reduce((groups, repo) => {
    const { priorityGroup } = repo;
    if (!groups[priorityGroup]) groups[priorityGroup] = [];
    groups[priorityGroup].push(repo);
    return groups;
  }, {});

  const priorityGroupsCount = Object.keys(groupedByPriority).length;

  useEffect(() => {
    localStorage.setItem("operationKey", operationId);
    return () => localStorage.removeItem("operationKey");
  }, [operationId]);

  const [repoStatuses, setRepoStatuses] = useState(
    selectedRepositories.reduce((acc, repo) => {
      acc[repo.id] = "initializing...";
      return acc;
    }, {})
  );

  const saveRepositories = useCallback(async () => {
    const description = "gitFlow release / hotfix management";
    const reposToSave = selectedRepositories.map((repo) => ({
      ...repo,
      status: repoStatuses[repo.id] || "initializing...",
    }));

    try {
      const saveResponse = await saveRelease({
        key: operationId,
        description,
        repositories: reposToSave.map((repo) => ({
          repositoryName: repo.name,
          repositoryId: repo.id,
          priorityGroup: repo.priorityGroup,
          tenantName: repo.tenantName,
        })),
        tenantName: localStorage.getItem("selectedTenant"),
      });

      if (saveResponse.status === 201) {
        setRepoStatuses((prev) => ({
          ...prev,
          ...reposToSave.reduce((acc, repo) => {
            acc[repo.id] = saveResponse.data;
            return acc;
          }, {}),
        }));
        return true;
      }
      return false;
    } catch (error) {
      console.error(error);
      return false;
    }
  }, [operationId, repoStatuses, selectedRepositories]);

  async function saveOrReset(loaded, operationId, saveRepositories) {
    let savedSuccessfully = "";
    let resetSuccessfully = "";

    if (loaded) {
      resetSuccessfully = await resetStatus(operationId);
    } else {
      savedSuccessfully = await saveRepositories();
    }
    return { savedSuccessfully, resetSuccessfully };
  }

  const handleAction = async (actionFn) => {
    const { savedSuccessfully, resetSuccessfully } = await saveOrReset(
      loaded,
      operationId,
      saveRepositories
    );

    if (savedSuccessfully || resetSuccessfully) {
      actionFn(operationId, "1").catch(console.error);

      const updatedRepos = selectedRepositories.map((repo) => ({
        ...repo,
        status: repoStatuses[repo.id] || "initializing...",
      }));

      navigate("/state", {
        state: {
          repositories: updatedRepos,
          key: operationId,
          priorityGroupsCount,
          onlyShowClose: false,
        },
      });
    }
  };

  const handleSaveAndNavigate = async () => {
    if (!loaded) {
      const saved = await saveRepositories();
      if (saved) {
        const updatedRepos = selectedRepositories.map((repo) => ({
          ...repo,
          status: repoStatuses[repo.id] || "initializing...",
        }));
        navigate("/state", {
          state: {
            repositories: updatedRepos,
            key: operationId,
            priorityGroupsCount,
            onlyShowClose: true,
          },
        });
      }
    }
  };

  return (
      <BaseBox
        sx={{
          height: "calc(100vh - 140px)", 
          display: "flex",
          flexDirection: "column",
          maxWidth: "1160px",
          mx: "auto",
          mt: 0,
          px: 2,
        }}
      >
        <BaseTypography sx={{ fontSize: "1.2rem", mb: 2, fontWeight: 600 }}>
          GitFlow Release / Hotfix Management
        </BaseTypography>

        {/* Display the Request ID */}
        <BaseBox sx={{ mb: 2 }}>
          <BaseTypography>
            <strong>Request ID:</strong> {operationId}
          </BaseTypography>
        </BaseBox>

        {/* Scrollable table container */}
        <BaseBox
          sx={{
            flexGrow: 1, // fill remaining vertical space
            overflowY: "auto", // enable vertical scroll
            mb: 2,
            pr: 1, // small right padding to avoid scrollbar overlap
          }}
        >
          {Object.entries(groupedByPriority).length > 0 ? (
            Object.entries(groupedByPriority).map(([priority, repos]) => (
              <BaseBox key={priority} sx={{ mb: 3 }}>
                <BaseTypography sx={{ fontWeight: 600, mb: 1 }}>
                  Priority Group: {priority}
                </BaseTypography>
                <BaseBox sx={{ overflowX: "auto" }}>
                  <table
                    className="repositories-table"
                    style={{ width: "100%" }}
                  >
                    <tbody>
                      {repos.map((repo) => (
                        <tr key={repo.id}>
                          <td>{repo.name}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </BaseBox>
              </BaseBox>
            ))
          ) : (
            <BaseTypography>
              No selected repositories to display.
            </BaseTypography>
          )}
        </BaseBox>

        {/* Action Buttons Panel */}
       <BaseBox
  sx={{
    border: "1px solid #ccc",
    borderRadius: "8px",
    p: 2,
    mb: 2,
    display: "flex",
    flexWrap: "wrap",
    gap: 2,
    backgroundColor: "#f9f9f9",
    justifyContent: "center", // center horizontally
    alignItems: "center",     // center vertically
  }}
>
          <BaseButton
            onClick={() => handleAction(openRelease)}
            color="primary"
            sx={{ width: "auto", minWidth: "140px" }}
          >
            Open Release
          </BaseButton>
          <BaseButton
            onClick={() => handleAction(closeRelease)}
            color="secondary"
            sx={{ width: "auto", minWidth: "140px" }}
          >
            Close Release
          </BaseButton>

          <BaseButton
            onClick={() => handleAction(releaseOpeningRollBack)}
            color="info"
            sx={{ width: "auto", minWidth: "180px" }}
          >
            Release Opening Roll-Back
          </BaseButton>
          <BaseButton
            onClick={() => handleAction(releaseClosingRollBack)}
            color="info"
            sx={{ width: "auto", minWidth: "180px" }}
          >
            Release Closing Roll-Back
          </BaseButton>
        </BaseBox>

<BaseBox
  sx={{
    border: "1px solid #ccc",
    borderRadius: "8px",
    p: 2,
    mb: 2,
    display: "flex",
    flexWrap: "wrap",
    gap: 2,
    backgroundColor: "#f9f9f9",
    justifyContent: "center", // center horizontally
    alignItems: "center",     // center vertically
  }}
>
          <BaseButton
            onClick={() => handleAction(openHotfix)}
            color="primary"
            sx={{ width: "auto", minWidth: "140px" }}
          >
            Open Hotfix
          </BaseButton>
          <BaseButton
            onClick={() => handleAction(closeHotfix)}
            color="secondary"
            sx={{ width: "auto", minWidth: "140px" }}
          >
            Close Hotfix
          </BaseButton>

          <BaseButton
            onClick={() => handleAction(hotfixOpeningRollBack)}
            color="info"
            sx={{ width: "auto", minWidth: "180px" }}
          >
            Hotfix Opening Roll-Back
          </BaseButton>
          <BaseButton
            onClick={() => handleAction(hotfixClosingRollBack)}
            color="info"
            sx={{ width: "auto", minWidth: "180px" }}
          >
            Hotfix Closing Roll-Back
          </BaseButton>
        </BaseBox>

        {/* Navigation Buttons Panel */}
        <BaseBox
          sx={{
            border: "1px solid #ccc",
            borderRadius: "8px",
            p: 2,
            display: "flex",
            gap: 2,
            justifyContent: "flex-end",
          }}
        >
          <BaseButton
            onClick={() => navigate(-1)}
            color="secondary"
            sx={{ minWidth: "100px" }}
          >
            Back
          </BaseButton>
          <BaseButton
            onClick={handleSaveAndNavigate}
            color="primary"
            sx={{ minWidth: "100px" }}
          >
            Save
          </BaseButton>
          <BaseButton
            onClick={() => navigate("/home")}
            color="secondary"
            sx={{ minWidth: "100px" }}
          >
            Close
          </BaseButton>
          <BaseButton
            onClick={() => navigate(-1)}
            color="secondary"
            sx={{ minWidth: "100px" }}
          >
            Cancel
          </BaseButton>
        </BaseBox>
      </BaseBox>
  );
}

export default PriorityGroups;
